import {Field, useFormikContext} from 'formik';

import {
    FormikRadioGroup,
    FormikSelect,
    FormikTextarea,
    FormikTextInput,
    PlatformFieldset,
} from '@flexinet/ui-components';
import {Application, ApplicationCategory} from '../types.ts';

type FormValues = Application;

// FNP-1498: CREATING AN APPLICATION IS A NAME AND A DESCRIPTION.
//
// An application record is a business record. Creating one writes a row;
// nothing is deployed, so no account, zone, certificate or repository field
// belongs here. Applications are built with the build-app skill (FNP-1675).
export const FormComponent = () => {

    const {values} = useFormikContext<FormValues>();

    // The add drawer passes `{}`, the edit drawer passes a loaded record. No
    // explicit mode prop is needed and adding one would be a second source of
    // truth for the same fact.
    const isEdit = Boolean(values.id);

    return (
        <>
            <PlatformFieldset legend={'Application Details'}>

                {/*
                  * ⚠️ NOT EDITABLE ON THE EDIT FORM, and shown as text rather
                  * than a disabled input so nobody tries.
                  *
                  * `name` is immutable — console-api's update resolver throws
                  * NameImmutable on the field's mere PRESENCE. This form used
                  * to render it as a live input that `buildUpdateApplicationInput`
                  * silently dropped, so a rename returned a green "Application
                  * updated" toast and changed nothing. A refusal would have
                  * been better than that.
                  */}
                {isEdit ? (
                    <Field
                        name='name'
                        label='Name'
                        description='The application name cannot be changed after creation.'
                        component={FormikTextInput}
                        disabled
                        my={'xl'}
                    />
                ) : (
                    <Field
                        name='name'
                        label='Name'
                        description='The name people see. It cannot be changed later, so choose one you can live with.'
                        component={FormikTextInput}
                        withAsterisk
                        my={'xl'}
                    />
                )}

                <Field
                    name='description'
                    label='Description'
                    description='One line on what this application is for.'
                    component={FormikTextarea}
                    withAsterisk
                    my={'xl'}
                />

            </PlatformFieldset>

            {/*
              * Everything below is a property of an application that already
              * exists. On create it is absent entirely — the record is written
              * with sensible defaults and the operator fills these in when they
              * have something to say.
              */}
            {isEdit ? (
                <PlatformFieldset legend={'Application Properties'}>

                    <Field
                        name='applicationCategory'
                        label='Category'
                        description='Who this application is for. Admin applications are hidden from non-admin users.'
                        component={FormikSelect}
                        data={[
                            {
                                label: ApplicationCategory.ADMIN,
                                value: ApplicationCategory.ADMIN
                            },
                            {
                                label: ApplicationCategory.BUSINESS,
                                value: ApplicationCategory.BUSINESS
                            },
                            {
                                label: ApplicationCategory.CONSUMER,
                                value: ApplicationCategory.CONSUMER
                            },
                        ]}
                        withAsterisk
                        my={'xl'}
                    />

                    <Field
                        name='applicationStatus'
                        label='Status'
                        description='Please enter application status'
                        component={FormikRadioGroup}
                        data={[
                            {
                                label: 'Active',
                                value: 'ACTIVE'
                            },
                            {
                                label: 'Inactive',
                                value: 'INACTIVE'
                            },
                        ]}
                        withAsterisk
                        my={'xl'}
                    />

                    {/*
                      * A pointer to where the application is reachable. Typing
                      * one here does not move any DNS.
                      */}
                    <Field
                        name='url'
                        label='URL'
                        description='Where the application is reachable.'
                        component={FormikTextInput}
                        my={'xl'}
                    />

                    <Field
                        name='logoUrl'
                        label='Logo URL'
                        description='Please enter logo url'
                        component={FormikTextInput}
                        my={'xl'}
                    />

                </PlatformFieldset>
            ) : null}
        </>
    );
};
